import { createDiscountCode } from "@/api/settings";
import CloseIcon from "@mui/icons-material/Close";
import {
    Box,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    InputAdornment,
    Stack,
    Toolbar,
    Typography,
} from "@mui/material";
import { FormButton, FormContainer, FormTextFieldElement } from "@rhf-kit/mui";
import { useMutation } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { enqueueSnackbar } from "notistack";

interface Props {
    open: boolean;
    setOpen: (open: boolean) => void;
    mutateDiscounts: () => void;
}

interface FormData {
    code: string;
    discount: string;
}

export const CreateDiscountModal = ({
    open,
    setOpen,
    mutateDiscounts,
}: Props) => {
    const createDiscount = useMutation({
        mutationFn: createDiscountCode,
        onSuccess: () => {
            mutateDiscounts();
            enqueueSnackbar(`Discount created successfully`, {
                variant: "success",
            });
            setOpen(false);
        },
        onError: (error: AxiosError<{ detail: string }>) => {
            if (
                error.response &&
                error.response.data &&
                error.response.data.detail
            ) {
                enqueueSnackbar(error.response.data.detail, {
                    variant: "error",
                });
            }
        },
    });

    const handleSubmit = (data: FormData) => {
        createDiscount.mutate({
            code: data.code,
            discount: parseFloat(data.discount) / 100.0,
        });
    };

    return (
        <Dialog
            fullWidth
            maxWidth="sm"
            open={open}
            onClose={() => setOpen(false)}
        >
            <DialogTitle>
                <Toolbar variant="dense">
                    <Typography variant="h6" fontWeight={600}>
                        Create a new discount code
                    </Typography>
                    <Box flexGrow={1} />
                    <IconButton edge="end" onClick={() => setOpen(false)}>
                        <CloseIcon />
                    </IconButton>
                </Toolbar>
            </DialogTitle>
            <DialogContent>
                <FormContainer
                    defaultValues={{ code: "", discount: "" }}
                    onSuccess={handleSubmit}
                >
                    <Stack direction={"column"} spacing={2} my={2}>
                        <FormTextFieldElement
                            id="code"
                            name="code"
                            label="Code"
                            size="small"
                            required
                        />
                        <FormTextFieldElement
                            id="discount"
                            name="discount"
                            label="Discount"
                            type="number"
                            rules={{
                                min: {
                                    value: 1,
                                    message: "Discount must be at least 1%",
                                },
                            }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment
                                        position="start"
                                        variant="standard"
                                    >
                                        %
                                    </InputAdornment>
                                ),
                            }}
                            size="small"
                            required
                        />
                    </Stack>
                    <FormButton variant="contained" color="primary">
                        Create Discount
                    </FormButton>
                </FormContainer>
            </DialogContent>
        </Dialog>
    );
};
